import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Filter, Grid, RefreshCw, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import axios from 'axios';
import toast from 'react-hot-toast';
import { createPortal } from 'react-dom';
import Header from '../components/Header';
import './MatrixDashboard.css';

// Paths relative to axios.defaults.baseURL (/api) — do NOT prefix REACT_APP_API_URL
// or requests become /api/api/... and fail.

const MatrixDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [substores, setSubstores] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [selectedHubs, setSelectedHubs] = useState([]);
  const [showHubDropdown, setShowHubDropdown] = useState(false);
  const [hubSearchTerm, setHubSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [matrixData, setMatrixData] = useState(null);
  const [matrixLoading, setMatrixLoading] = useState(false);

  // Master filters state
  const [masterFilters, setMasterFilters] = useState({
    excludePlacedOrderLast3Days: false,
    excludeWhatsappLast7Days: false,
    excludeEmailLast7Days: false,
    excludeSMSSentLast7Days: false
  });

  // Selected checkboxes state for download functionality
  const [selectedCells, setSelectedCells] = useState(new Set());

  useEffect(() => {
    fetchFilters();
    // Don't automatically load matrix data on page load
    // Matrix data will only show when filters are applied
  }, []);

  // Close hub dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showHubDropdown && 
          !event.target.closest('.hub-dropdown-container') && 
          !event.target.closest('.hub-dropdown')) {
        setShowHubDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHubDropdown]);

  const fetchFilters = async () => {
    setLoading(true);
    try {
      console.log('Fetching filters from: /matrix/filters');
      const response = await axios.get('/matrix/filters');
      console.log('Filters response:', response.data);
      if (response.data.success) {
        setSubstores(response.data.data.substores);
        setHubs(response.data.data.hubs);
        console.log('Substores set:', response.data.data.substores);
        console.log('Hubs set:', response.data.data.hubs);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
      toast.error('Failed to load filters');
    } finally {
      setLoading(false);
    }
  };

  const handleMasterFilterChange = (filterName, value) => {
    setMasterFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleHubSelect = (hub) => {
    setSelectedHubs(prev => {
      if (prev.includes(hub)) {
        // Remove if already selected
        return prev.filter(h => h !== hub);
      } else {
        // Add if not selected
        return [...prev, hub];
      }
    });
    setHubSearchTerm('');
  };

  const handleHubDropdownToggle = () => {
    if (!showHubDropdown) {
      // Calculate position when opening dropdown
      const selector = document.querySelector('.hub-selector');
      if (selector) {
        const rect = selector.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 4,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    }
    setShowHubDropdown(!showHubDropdown);
  };

  const handleHubRemove = (hub) => {
    setSelectedHubs(prev => prev.filter(h => h !== hub));
  };

  const handleSelectAllHubs = () => {
    setSelectedHubs(filteredHubs.map(h => h));
  };

  const handleClearAllHubs = () => {
    setSelectedHubs([]);
  };

  const clearAllFilters = () => {
    setSelectedHubs([]);
    setMasterFilters({
      excludePlacedOrderLast3Days: false,
      excludeWhatsappLast7Days: false,
      excludeEmailLast7Days: false,
      excludeSMSSentLast7Days: false
    });
    // Clear matrix data when filters are cleared
    setMatrixData(null);
    setSelectedCells(new Set());
  };

  const loadMatrixData = async (showToast = false) => {
    setMatrixLoading(true);
    try {
      // Convert selected hubs to substores
      const selectedSubstores = [];
      selectedHubs.forEach(hubName => {
        const hub = hubs.find(h => h.hub === hubName);
        if (hub) {
          selectedSubstores.push(...hub.substores);
        }
      });

      const response = await axios.post('/matrix/apply-filters', {
        substores: selectedSubstores.length > 0 ? selectedSubstores : null,
        ...masterFilters
      });

      if (response.data.success) {
        setMatrixData(response.data.data);
        setLastUpdated(new Date());
        if (showToast) {
          const filterMessage = selectedHubs.length > 0 
            ? `Matrix data loaded for ${selectedHubs.length} hub(s)` 
            : 'Matrix data loaded for all hubs';
          toast.success(filterMessage);
        }
      } else {
        if (showToast) {
        toast.error(response.data.message || 'Failed to load matrix data');
        }
      }
    } catch (error) {
      console.error('Error loading matrix data:', error);
      if (showToast) {
      toast.error('Failed to load matrix data');
      }
    } finally {
      setMatrixLoading(false);
    }
  };

  const applyFilters = async () => {
    await loadMatrixData(true); // Show toast when manually applying filters
  };

  const filteredHubs = hubs.filter(hub =>
    hub.hub && hub.hub.toLowerCase().includes(hubSearchTerm.toLowerCase())
  );

  // Handle checkbox selection for download
  const handleCellSelection = (periodId, columnId) => {
    const cellKey = `${periodId}-${columnId}`;
    setSelectedCells(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cellKey)) {
        newSet.delete(cellKey);
      } else {
        newSet.add(cellKey);
      }
      return newSet;
    });
  };

  // Download selected customer details as CSV
  const downloadSelectedData = async () => {
    if (selectedCells.size === 0) {
      toast.error('Please select at least one cell to download');
      return;
    }

    if (!matrixData) {
      toast.error('No matrix data available');
      return;
    }

    // Convert selected hubs to substores for download
    const selectedSubstores = [];
    selectedHubs.forEach(hubName => {
      const hub = hubs.find(h => h.hub === hubName);
      if (hub) {
        selectedSubstores.push(...hub.substores);
      }
    });

    try {
      // Show loading state
      toast.loading('Fetching customer details...', { id: 'download' });

      // Prepare selected filters for API
      const selectedFilters = Array.from(selectedCells).map(cellKey => {
        const [periodId, columnId] = cellKey.split('-');
        return { periodId, columnId };
      });

      // Fetch customer details from API
      const response = await axios.post('/matrix/customer-details', {
        substores: selectedSubstores,
        ...masterFilters,
        selectedFilters
      });

      if (response.data.success) {
        const { customers } = response.data.data;
        
        if (customers.length === 0) {
          toast.error('No customers found for selected filters', { id: 'download' });
          return;
        }

        // Prepare CSV data with customer details
        const csvData = [];
        const headers = [
          'Customer Name',
          'Email',
          'Phone',
          'Substore',
          'Order Count',
          'Login Count',
          'Total Orders Value',
          'Last Order Date',
          'Last Login Date',
          'Matched Filter Period',
          'Matched Filter Segment',
          'Customer ID'
        ];
        csvData.push(headers);

        // Add customer data
        customers.forEach(customer => {
          const lastOrder = customer.orders && customer.orders.length > 0 
            ? customer.orders[customer.orders.length - 1] 
            : null;
          
          const totalOrderValue = customer.lifetimeGMV || 0;

          // Generate initials from full name
          const generateInitials = (fullName) => {
            if (!fullName || fullName === 'N/A') return 'N/A';
            const nameParts = fullName.trim().split(' ');
            if (nameParts.length >= 2) {
              return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
            } else if (nameParts.length === 1) {
              return nameParts[0][0].toUpperCase();
            }
            return 'N/A';
          };

          csvData.push([
            generateInitials(customer.full_name),
            customer.emails && customer.emails.length > 0 ? customer.emails[0] : 'N/A',
            customer.phone_numbers && customer.phone_numbers.length > 0 ? customer.phone_numbers[0] : 'N/A',
            customer.substore || 'N/A',
            customer.totalOrders || 0,
            customer.totalLogins || 0,
            totalOrderValue,
            customer.lastOrderDate ? new Date(customer.lastOrderDate).toLocaleDateString() : 'N/A',
            customer.lastLoginAt ? new Date(customer.lastLoginAt).toLocaleDateString() : 'N/A',
            customer.matchedFilter?.periodLabel || 'N/A',
            customer.matchedFilter?.columnLabel || 'N/A',
            customer._id || 'N/A'
          ]);
        });

        // Convert to CSV string
        const csvContent = csvData.map(row => 
          row.map(cell => `"${cell}"`).join(',')
        ).join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `customer_details_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success(`Downloaded ${customers.length} customer details`, { id: 'download' });
      } else {
        toast.error(response.data.message || 'Failed to fetch customer details', { id: 'download' });
      }
    } catch (error) {
      console.error('Error downloading customer details:', error);
      toast.error('Failed to download customer details', { id: 'download' });
    }
  };

  // Generate time periods for matrix rows
  const generateTimePeriods = () => {
    const periods = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    // Add recent time periods
    periods.push({ id: 'total', label: 'Total', type: 'total' });
    periods.push({ id: '3days', label: '3 days', type: 'recent' });
    periods.push({ id: '4-7days', label: '4-7 days', type: 'recent' });
    periods.push({ id: '8-14days', label: '8-14 days', type: 'recent' });
    periods.push({ id: 'remaining', label: 'Remaining month', type: 'recent' });

    // Add months from January to current month
    for (let month = 0; month <= currentMonth; month++) {
      const monthName = new Date(currentYear, month).toLocaleString('default', { month: 'long' });
      periods.push({ 
        id: `${monthName}${currentYear}`, 
        label: `${monthName} ${currentYear}`, 
        type: 'month' 
      });
    }

    // Add years from current year down to 2021 (descending order)
    for (let year = currentYear; year >= 2021; year--) {
      periods.push({ id: `${year}`, label: `${year}`, type: 'year' });
    }

    return periods;
  };

  const timePeriods = generateTimePeriods();

  // Column definitions
  const columns = [
    { 
      id: '3plus_orders', 
      label: '3 or more orders', 
      subLabel: 'Frequent Buyer',
      type: 'orders'
    },
    { 
      id: '2_orders', 
      label: '2 orders', 
      subLabel: 'Occasional Buyer',
      type: 'orders'
    },
    { 
      id: '1_order', 
      label: '1 order', 
      subLabel: 'One Time Buyer',
      type: 'orders'
    },
    { 
      id: '5plus_logins', 
      label: '5 or more logins', 
      subLabel: 'Frequent Visitor',
      type: 'logins'
    },
    { 
      id: '2to4_logins', 
      label: '2 to 4 logins', 
      subLabel: 'Occasional Visitor',
      type: 'logins'
    },
    { 
      id: '1_login', 
      label: '1 login', 
      subLabel: 'OT Visitor',
      type: 'logins'
    }
  ];

  // Function to get total count for a column from matrix data
  const getColumnTotalCount = (columnId) => {
    if (!matrixData) return 0;
    
    let total = 0;
    Object.keys(matrixData).forEach(periodId => {
      if (matrixData[periodId] && matrixData[periodId][columnId]) {
        total += matrixData[periodId][columnId];
      }
    });
    return total;
  };

  return (
    <div className="matrix-dashboard-page">
      <Header
        title="Matrix Dashboard"
        onRefresh={fetchFilters}
        loading={loading}
        lastUpdated={lastUpdated}
      />

      <div className="matrix-content">
        {/* Master Filters Section */}
        <motion.div 
          className="master-filters-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="filters-header">
            <h2>Master Filters</h2>
            <p>Configure advanced filtering options for matrix analysis</p>
          </div>

          <div className="filters-container">
            {/* Horizontal Layout: Hub Filter + All Exclusion Filters */}
            <div className="filters-horizontal-layout">
              {/* Hub Filter */}
            <div className="hub-filter-section">
                <div className={`filter-group hub-filter-group ${selectedHubs.length > 0 ? 'applied' : ''} ${showHubDropdown ? 'dropdown-open' : ''}`}>
                  <h4>Hub *</h4>
                  
                  {/* Selected Hubs Display */}
                  {selectedHubs.length > 0 && (
                    <div className="selected-hubs-container">
                      {selectedHubs.map(hub => (
                        <div key={hub} className="selected-hub-tag">
                          <span className="hub-tag-text">{hub}</span>
                          <button 
                            onClick={() => handleHubRemove(hub)}
                            className="hub-tag-remove"
                            title="Remove hub"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className={`hub-dropdown-container ${showHubDropdown ? 'dropdown-open' : ''}`}>
                    <div 
                      className="hub-selector"
                      onClick={handleHubDropdownToggle}
                    >
                      <div className="hub-display">
                        <span className="placeholder">
                          {hubs.length > 0 ? 
                            (selectedHubs.length > 0 ? 
                              `Select more hubs... (${selectedHubs.length} selected)` : 
                              'Select hubs...') : 
                            'Loading hubs...'}
                        </span>
                    </div>
                      <div className="hub-arrow">▼</div>
                  </div>
                  
                    {showHubDropdown && createPortal(
                      <div 
                        className="hub-dropdown-portal"
                        style={{
                          position: 'fixed',
                          top: `${dropdownPosition.top}px`,
                          left: `${dropdownPosition.left}px`,
                          width: `${dropdownPosition.width}px`,
                          zIndex: 99999
                        }}
                      >
                        <div className="hub-dropdown">
                          <div className="hub-search">
                        <input
                          type="text"
                              placeholder="Search hubs..."
                              value={hubSearchTerm}
                              onChange={(e) => setHubSearchTerm(e.target.value)}
                              className="hub-search-input"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      
                          {/* Bulk Actions */}
                          {filteredHubs.length > 0 && (
                            <div className="hub-bulk-actions">
                              <button 
                                onClick={handleSelectAllHubs}
                                className="bulk-action-btn select-all"
                              >
                                Select All
                              </button>
                              <button 
                                onClick={handleClearAllHubs}
                                className="bulk-action-btn clear-all"
                              >
                                Clear All
                              </button>
                            </div>
                          )}
                          
                          <div className="hub-options">
                            {hubs.length === 0 ? (
                              <div className="no-hubs">Loading hubs...</div>
                            ) : filteredHubs.length === 0 ? (
                              <div className="no-hubs">No hubs found matching "{hubSearchTerm}"</div>
                            ) : (
                              filteredHubs.map(hub => (
                                <div 
                                  key={hub.hub} 
                                  className={`hub-option ${selectedHubs.includes(hub.hub) ? 'selected' : ''}`}
                                  onClick={() => handleHubSelect(hub.hub)}
                                >
                                  <div className="hub-option-content">
                                    <input
                                      type="checkbox"
                                      checked={selectedHubs.includes(hub.hub)}
                                      onChange={() => {}} // Handled by onClick
                                      className="hub-checkbox"
                                    />
                                    <span className="hub-label">{hub.hub}</span>
                                  </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                      </div>,
                      document.body
                  )}
                </div>
              </div>
            </div>

              {/* Exclusion Filters - All Visible */}
            <div className="exclusion-filters-section">
              <h4>Exclusion Filters</h4>
              <div className="exclusion-filters-grid">
                <div className={`filter-group checkbox-filter ${masterFilters.excludePlacedOrderLast3Days ? 'applied' : ''}`}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={masterFilters.excludePlacedOrderLast3Days}
                      onChange={(e) => handleMasterFilterChange('excludePlacedOrderLast3Days', e.target.checked)}
                      className="checkbox-input"
                    />
                    <span className="checkbox-text">Exclude placed order in last 3 days</span>
                  </label>
                </div>

                <div className={`filter-group checkbox-filter ${masterFilters.excludeWhatsappLast7Days ? 'applied' : ''}`}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={masterFilters.excludeWhatsappLast7Days}
                      onChange={(e) => handleMasterFilterChange('excludeWhatsappLast7Days', e.target.checked)}
                      className="checkbox-input"
                    />
                    <span className="checkbox-text">Exclude WhatsApp sent in last 7 days</span>
                  </label>
                </div>

                <div className={`filter-group checkbox-filter ${masterFilters.excludeEmailLast7Days ? 'applied' : ''}`}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={masterFilters.excludeEmailLast7Days}
                      onChange={(e) => handleMasterFilterChange('excludeEmailLast7Days', e.target.checked)}
                      className="checkbox-input"
                    />
                    <span className="checkbox-text">Exclude email sent in last 7 days</span>
                  </label>
                </div>

                <div className={`filter-group checkbox-filter ${masterFilters.excludeSMSSentLast7Days ? 'applied' : ''}`}>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={masterFilters.excludeSMSSentLast7Days}
                      onChange={(e) => handleMasterFilterChange('excludeSMSSentLast7Days', e.target.checked)}
                      className="checkbox-input"
                    />
                    <span className="checkbox-text">Exclude SMS sent in last 7 days</span>
                  </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter Actions and Status Container */}
            <div className="filter-actions-status-container">
            {/* Filter Actions */}
            <div className="filter-actions">
              <button
                onClick={applyFilters}
                className="apply-filters-btn"
                  disabled={matrixLoading}
              >
                {matrixLoading ? (
                  <RefreshCw size={16} className="spinning" />
                ) : (
                  <Filter size={16} />
                )}
                {matrixLoading ? 'Loading...' : 'Apply Filters'}
              </button>

              <button
                onClick={clearAllFilters}
                className="clear-filters-btn"
                disabled={matrixLoading}
              >
                Clear All Filters
              </button>
            </div>

            {/* Filter Status */}
            <div className="filter-status">
              <p>
                  <strong>Active Filters:</strong> {selectedHubs.length > 0 ? `${selectedHubs.length} hub(s) selected` : 'All hubs included'} • 
                {Object.values(masterFilters).filter(Boolean).length} exclusion filters active
              </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Matrix Table */}
        <motion.div 
          className="matrix-table-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {matrixData ? (
            <div className="matrix-table-container">
              <div className="matrix-table-header">
                <div className="matrix-header-content">
                  <div className="matrix-title-section">
                <h3>Customer Matrix Analysis</h3>
                <p>Data based on orders and login patterns across time periods</p>
                  </div>
                  <div className="matrix-actions">
                    <button
                      onClick={downloadSelectedData}
                      className="download-btn"
                      disabled={selectedCells.size === 0}
                      title={selectedCells.size > 0 ? `Download customer details for ${selectedCells.size} selected filters` : 'Select cells to download customer details'}
                    >
                      <Download size={16} />
                      Download Customer Details ({selectedCells.size})
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="matrix-table-wrapper">
                <table className="matrix-table">
                  <thead>
                    <tr>
                      <th className="row-header-cell">Time Period</th>
                      {columns.map(column => (
                        <th key={column.id} className="column-header">
                          <div className="column-title">{column.label}</div>
                          <div className="column-subtitle">{column.subLabel}</div>
                          <div className="column-value">{getColumnTotalCount(column.id).toLocaleString()}</div>
                        </th>
                      ))}
                      <th className="total-column">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timePeriods.map(period => (
                      <tr key={period.id} className={`matrix-row ${period.type}`}>
                        <td className="row-header-cell">{period.label}</td>
                        {columns.map(column => {
                          const cellData = matrixData[period.id]?.[column.id] || 0;
                          const cellKey = `${period.id}-${column.id}`;
                          const isSelected = selectedCells.has(cellKey);
                          const hasData = cellData > 0;
                          
                          return (
                            <td key={cellKey} className="matrix-cell">
                              <input 
                                type="checkbox" 
                                className="matrix-checkbox"
                                checked={isSelected}
                                disabled={!hasData}
                                onChange={() => handleCellSelection(period.id, column.id)}
                              />
                              <span className="cell-count">{cellData.toLocaleString()}</span>
                            </td>
                          );
                        })}
                        <td className="total-cell">
                          {(() => {
                            const rowTotal = columns.reduce((sum, column) => {
                              return sum + (matrixData[period.id]?.[column.id] || 0);
                            }, 0);
                            return rowTotal.toLocaleString();
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="matrix-content-area">
              <div className="content-placeholder">
                <Grid size={48} />
                <h3>Matrix Analysis</h3>
                <p>{matrixLoading ? 'Loading matrix data...' : 'Apply filters to view matrix data'}</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default MatrixDashboard;
